import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { BizEntityFunctionTypes, LoggerService } from '@optima-stride-ai/core';
import { LibraryService } from '../pages/libraries/library.service';
import { CollaboraService } from './collabora.service';
import { ThumbnailManagerService } from './thumbnail-manager.service';
import { Html2CanvasThumbnailHelper } from '../shared/utils/html2canvas-thumbnail.helper';
import { PdfThumbnailGenerator } from '../shared/utils/pdf-thumbnail-generator';
import { FileCategoryUtils, UIMessages } from '../shared/enums/app-constants';


/**
 * FileUploadService
 * 
 * Handles file upload operations with automatic thumbnail generation
 * - Dynamic file uploads with thumbnail processing
 * - Support for Collabora files, PDFs, and other file types
 * - Automatic thumbnail generation without caching
 */
@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  private libraryService = inject(LibraryService);
  private collaboraService = inject(CollaboraService);
  private thumbnailManagerService = inject(ThumbnailManagerService);
  private logger = inject(LoggerService);

  /**
   * Perform dynamic upload with automatic thumbnail generation and processing
   * 
   * @param folderId - Target folder ID for upload
   * @param files - Array of files to upload
   * @param isTemplate - Flag indicating if the upload is for a template
   * @returns Observable with upload results
   */
  async performDynamicUpload(folderId: string, files: File[], isTemplate?: boolean, params?: any): Promise<Observable<any>> {
    if (!folderId || files.length === 0) {
      this.logger.warn('❌ No folder or files selected for dynamic upload');
      throw new Error('No folder or files selected for dynamic upload');
    }

    await this.validateStructuralParentEntity(folderId);

    const fileName = files[0].name;
    const nameWithoutExt = fileName.split('.').slice(0, -1).join('.');
    
    this.logger.debug(`📤 Starting dynamic upload for file: ${fileName}`);

    // Generate thumbnail based on file type
    const thumbnail = await this.generateThumbnailForFile(files[0], fileName, nameWithoutExt);
    
    // Create zip file with thumbnail if available
    const zipBlob = await this.createUploadBlob(files[0], thumbnail, nameWithoutExt, fileName);
    
    // Perform the upload
    return this.libraryService.uploadMultipleFiles(folderId, files, zipBlob, isTemplate, params);
  }

  private async validateStructuralParentEntity(folderId: string): Promise<void> {
    if (!folderId) {
      return;
    }

    try {
      const parentEntity = await this.libraryService.getBizEntityById(folderId);
      if (parentEntity?.type?.functionType === BizEntityFunctionTypes.Operational) {
        this.logger.warn(`⚠️ Upload blocked. Parent entity is operational: ${folderId}`);
        throw new Error(UIMessages.STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD);
      }
    } catch (error) {
      if (error instanceof Error && error.message === UIMessages.STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD) {
        throw error;
      }

      this.logger.warn(`⚠️ Could not validate parent entity type for upload target ${folderId}: ${error}`);
    }
  }

  /**
   * Generate thumbnail for a file based on its type
   * 
   * @param file - File to generate thumbnail for
   * @param fileName - Name of the file
   * @param nameWithoutExt - File name without extension
   * @returns Promise with base64 thumbnail string or empty string
   */
  private async generateThumbnailForFile(
    file: File, 
    fileName: string, 
    nameWithoutExt: string
  ): Promise<string> {
    let thumbnail = '';
    
    try {
      // Check if the file is a Collabora-supported file
            const fileCategory = FileCategoryUtils.getCategoryFromFilename(fileName);
         if (!fileCategory) {
        this.logger.warn(`⚠️ Could not determine category for file: ${fileName}`);
        return '';
      }
      const isCollaboraFile = this.collaboraService.isFileSupported(fileCategory);
      
      if (isCollaboraFile) {
        // Use Collabora thumbnail API for Collabora-supported files
        thumbnail = await this.generateCollaboraThumbnail(file, fileName);
      } else if (PdfThumbnailGenerator.isPdfFile(file)) {
        // Generate PDF thumbnail if the file is a PDF
        thumbnail = await this.generatePdfThumbnail(file, fileName);
      } else {
        // Use ThumbnailManagerService for all other file types
        thumbnail = await this.generateStandardThumbnail(file, fileName);
      }
      
      // Thumbnail generated without caching
      if (thumbnail && thumbnail.length > 0) {
        this.logger.debug('✅ Thumbnail generated successfully');
      }
    } catch (error) {
      this.logger.error(`❌ Error generating thumbnail: ${error}`);
      thumbnail = '';
    }
    
    return thumbnail;
  }

  /**
   * Generate thumbnail using Collabora API
   */
  private async generateCollaboraThumbnail(file: File, fileName: string): Promise<string> {
    this.logger.debug(`🖼️ Generating Collabora thumbnail for file: ${fileName}`);
    
    const thumbnailResult = await this.collaboraService.generateThumbnailForCollaboraFile(file);
    
    if (thumbnailResult) {
      this.logger.debug('✅ Collabora thumbnail generated successfully');
      return thumbnailResult;
    } else {
      this.logger.warn('⚠️ Collabora thumbnail generation failed');
      return '';
    }
  }

  /**
   * Generate thumbnail for PDF files
   */
  private async generatePdfThumbnail(file: File, fileName: string): Promise<string> {
    this.logger.debug('🖼️ Generating PDF thumbnail...');
    
    const thumbnailResult = await this.thumbnailManagerService.generateThumbnailForFile(file);
    
    if (thumbnailResult) {
      this.logger.debug('✅ PDF thumbnail generated successfully');
      return thumbnailResult;
    } else {
      this.logger.warn('⚠️ PDF thumbnail generation failed');
      return '';
    }
  }

  /**
   * Generate standard thumbnail using ThumbnailManagerService
   */
  private async generateStandardThumbnail(file: File, fileName: string): Promise<string> {
    this.logger.debug(`🖼️ Generating thumbnail for file: ${fileName}`);
    
    const thumbnailResult = await this.thumbnailManagerService.generateThumbnailForFile(file);
    
    if (thumbnailResult) {
      this.logger.debug('✅ Thumbnail generated successfully');
      return thumbnailResult;
    } else {
      this.logger.warn('⚠️ Thumbnail generation failed');
      return '';
    }
  }

  /**
   * Create upload blob (zip file) with thumbnail and main file
   * 
   * @param file - Main file to upload
   * @param thumbnail - Base64 thumbnail string
   * @param nameWithoutExt - File name without extension
   * @param fileName - Full file name
   * @returns Promise with Blob containing zipped files
   */
  private async createUploadBlob(
    file: File, 
    thumbnail: string, 
    nameWithoutExt: string,
    fileName: string
  ): Promise<Blob> {
    if (thumbnail && thumbnail.length > 0) {
      // Create thumbnail file and generate zip with both files
      const thumbnailFile = this.libraryService.base64ToFile(
        thumbnail, 
        `${nameWithoutExt || 'document'}-thumb.jpg`
      );
      return await Html2CanvasThumbnailHelper.generateZipFile(thumbnailFile, file);
    } else {
      // Create a simple zip with just the main file
      this.logger.warn('⚠️ No thumbnail generated, uploading file without thumbnail');
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      zip.file(fileName, file);
      return await zip.generateAsync({ type: "blob" });
    }
  }
}
